import { useBoolean } from 'ahooks';
import { message } from 'antd';
import React, { FC } from 'react';
import { UseMutationResult } from 'react-query';
import { FieldSectionType } from 'api/field-sections/types';
import { FieldPayload } from 'api/fields';
import { Field } from 'api/tasks';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import { NotFound } from 'components/atoms/result';
import { FieldFormDrawer } from 'components/organisms/module-settings/fields/drawer';
import { FieldForm } from 'components/organisms/module-settings/fields/form';
import { valuesToPayload } from 'components/organisms/module-settings/fields/utils';
import { RoutePaths } from 'constant';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';

interface FieldFormPageProps {
  isEdit?: boolean;
  title?: string;
  isLoading?: boolean;
  onClose: () => void;
  objectType: Field['objectType'];
  field?: Field;
  mutation: UseMutationResult<Field, unknown, FieldPayload>;
}

export interface FieldPageProps {
  objectType: Field['objectType'];
  title?: string;
  onClose: () => void;
}

export const FieldFormPage: FC<FieldFormPageProps> = (props) => {
  const {
    title,
    onClose,
    objectType,
    field,
    isEdit = false,
    isLoading,
    mutation,
  } = props;
  const [form] = Form.useForm();
  const [visible, { setFalse: closeDrawer }] = useBoolean(true);
  const errorMessage = useTranslation('settings.fields.create.error.title');

  const handleAfterVisibleChange = (isDrawerVisible: boolean) => {
    if (!isDrawerVisible) {
      onClose();
    }
  };

  const submitForm = async () => {
    try {
      const values = await form.validateFields();

      try {
        await mutation.mutateAsync(valuesToPayload(values), {
          onError: () => {
            message.error(errorMessage);
          },
        });
        closeDrawer();
      } catch (error) {
        logger.error('fields mutation failed', { error });
      }
    } catch (error) {
      logger.log('fields form validation failed', { error });
    }
  };

  const canShowForm = isEdit ? Boolean(field) : true;
  const subTitle = useTranslation('settings.fields.field.404.message');
  const actionText = useTranslation('settings.fields.field.404.action');

  return (
    <FieldFormDrawer
      hideFooter={isLoading || !canShowForm}
      title={title}
      visible={visible}
      isEdit={isEdit}
      isSubmitting={mutation.isLoading}
      onOk={submitForm}
      onCancel={closeDrawer}
      onClose={closeDrawer}
      afterVisibleChange={handleAfterVisibleChange}
    >
      {isLoading ? (
        <PageLoading />
      ) : canShowForm ? (
        <FieldForm form={form} field={field} objectType={objectType} />
      ) : (
        <NotFound
          subTitle={subTitle}
          linkTo={
            objectType === FieldSectionType.Project
              ? RoutePaths.projectFields
              : objectType === FieldSectionType.Task
              ? RoutePaths.taskFields
              : RoutePaths.teamMemberFields
          }
          actionText={actionText}
        />
      )}
    </FieldFormDrawer>
  );
};
